import { useQuery } from '@apollo/client';

import { getProductTypeFromTypename } from 'bundles/collections-carousel/constants';
import type { CarouselCollection, CollectionEntity } from 'bundles/collections-carousel/types/CarouselCollection';
import QueryCollection from 'bundles/front-page/queries/LohpCollection.graphql';
import type {
  LohpCollectionQuery,
  LohpCollectionQueryVariables,
  LohpRecommendationCollectionFragment,
} from 'bundles/front-page/queries/__generated__/LohpCollection';

export type UseCollectionOptions = {
  queryCollectionId: string;
  additionalInput?:
    | {
        preset_collection_id?: string;
      }
    | {
        collection_name?: string;
        facets?: Array<Array<string>>;
      };
  limit?: number;
  skip?: boolean;
};

export const normalizeCollection = (content?: LohpRecommendationCollectionFragment): CarouselCollection | null => {
  if (!content?.entities?.elements) {
    return null;
  }

  const normalizedEntities: CollectionEntity[] = content.entities.elements.flatMap((entity) => {
    if (entity?.__typename === 'ProductCard_ProductCard') {
      const { productTypeAttributes, marketingProductType } = entity;

      if ('canonical' in productTypeAttributes && productTypeAttributes.canonical) {
        const canonical = productTypeAttributes.canonical;

        let productCardImageUrl;
        if (canonical.__typename === 'Specialization_Specialization') productCardImageUrl = canonical.cardImageUrl;
        if (canonical.__typename === 'Course_Course') productCardImageUrl = canonical.promoPhotoUrl;

        let productUrl;

        if (canonical.__typename === 'Specialization_Specialization') productUrl = canonical.urlPath;
        if (canonical.__typename === 'Course_Course') productUrl = '/learn/' + canonical.slug;

        return [
          {
            id: canonical.id,
            name: canonical.name,
            slug: canonical.slug,
            imageUrl: productCardImageUrl || '',
            avgProductRating: productTypeAttributes.rating,
            numProductRatings: productTypeAttributes.reviewCount,
            coBrandingEnabled:
              canonical.__typename === 'Specialization_Specialization' ? canonical.coBrandingEnabled : undefined,
            partners:
              canonical.partners?.map((partner) => ({
                id: partner.id,
                name: partner.name,
                logo: partner.logo || '',
              })) || [],
            partnerLogos: canonical.partners?.map((partner) => partner.squareLogo || '') || [],
            link: productUrl || '',
            productType: getProductTypeFromTypename(marketingProductType),
          },
        ] as CollectionEntity[];
      }
    }

    return [];
  });

  const collection: CarouselCollection = {
    collectionId: content.id,
    recommender: {
      hash: content.recommenderConfigurationHash || '',
      ...(content.context && { context: content.context }),
    },
    title: '',
    entities: normalizedEntities,
  };

  return collection;
};

/**
 * This hook calls Recommendations.queryCollection to fetch collection data (by collection ID) and normalizes the response
 * into a CarouselCollection object.
 *
 * @param {string} queryCollectionId - (Required) ID of the collection to fetch
 * @param {JSON} additionalInput - (Optional) Additional input for the query
 * @property {string} [preset_collection_id] - (Optional) For preset collections
 * @param {number} [limit] - (Optional) Limit the number of entities returned [default: 50]
 * @param {boolean} skip - (Optional) Skip the query [default: false if queryCollectionId is provided]
 */
function useLohpCollection({ queryCollectionId, additionalInput, limit = 12, skip = false }: UseCollectionOptions) {
  const { loading, data, error } = useQuery<LohpCollectionQuery, LohpCollectionQueryVariables>(QueryCollection, {
    variables: {
      queryCollectionId,
      additionalInput,
      limit,
    },
    skip: skip || !queryCollectionId,
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all' as const,
    context: { clientName: 'gatewayGql' },
    ssr: false, // Intentionally client side to speed up page load
  });

  if (skip) {
    return {
      skipped: true,
      loading: false,
      collection: null,
      error: null,
    };
  }

  return {
    skipped: false,
    loading,
    collection: normalizeCollection(data?.Recommendations?.queryCollection),
    error,
  };
}

export default useLohpCollection;
